import React, { type FormEvent, useEffect, useMemo, useState } from 'react';
import ForgotPasswordTemplate from './components/auth/templates/ForgotPasswordTemplate';
import LoginTemplate from './components/auth/templates/LoginTemplate';
import ResetPasswordTemplate from './components/auth/templates/ResetPasswordTemplate';
import SignupTemplate from './components/auth/templates/SignupTemplate';
import VerifyEmailTemplate from './components/auth/templates/VerifyEmailTemplate';
import { AnimatedScribble, type ScribbleType } from './components/AnimatedScribble';
import type {
    AuthErrorType,
    AuthFeedback,
    AuthTemplateProps,
    DirtyFields,
    FormErrors,
    FormField,
    FormState,
    Mode,
    PasswordRequirement,
    TouchedFields,
} from './components/auth/types';
import {
    isFieldValueEmpty,
    resolveFieldValidationState,
    type ValidationField,
} from './components/auth/fieldValidation';
import SiteFooter from './components/SiteFooter';
import SiteHeader from './components/SiteHeader';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useLocation, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

type AppConfig = {
    client: {
        id: 'spark' | 'halo' | 'lumen';
        label: string;
        signupPolicy: 'auto_login' | 'verify_first' | 'disabled';
    };
    oauthProviders?: Array<{ id: 'google' | 'apple'; label: string; logo?: string }>;
    returnTo: string;
};

type DevBuildVersionResponse = {
    version?: string;
};

type SessionStatusResponse = {
    authenticated?: boolean;
    redirectTo?: string;
};

type AuthRequestError = Error & {
    code?: AuthErrorType;
    status?: number;
};

const developmentRefreshIntervalMs = 500;

const formFields: FormField[] = [
    'firstName',
    'lastName',
    'email',
    'password',
    'confirmPassword',
    'mfaCode',
    'mfaRecovery',
];

const passwordRequirements: PasswordRequirement[] = [
    {
        id: 'length',
        label: 'At least 12 characters',
        message: 'Password must be at least 12 characters',
        test: (value) => value.length >= 12,
    },
    {
        id: 'letter',
        label: 'Includes a letter',
        message: 'Password must include at least one letter',
        test: (value) => /[A-Za-z]/.test(value),
    },
    {
        id: 'number',
        label: 'Includes a number',
        message: 'Password must include at least one number',
        test: (value) => /\d/.test(value),
    },
    {
        id: 'symbol',
        label: 'Includes a symbol (e.g. !, #, ?)',
        message: 'Password must include at least one symbol',
        test: (value) => /[^A-Za-z0-9\s]/.test(value),
    },
];

const authBackgroundScribbleType: ScribbleType = 'rainbow';

const lengthRule = passwordRequirements.find((rule) => rule.id === 'length');
const PasswordSchema = z
    .string()
    .min(12, lengthRule?.message || 'Password must be at least 12 characters')
    .superRefine((value, ctx) => {
        passwordRequirements
            .filter((rule) => rule.id !== 'length')
            .forEach((rule) => {
                if (!rule.test(value)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: rule.message,
                    });
                }
            });
    });
const FirstNameSchema = z
    .string()
    .trim()
    .min(1, 'Enter your first name')
    .max(80, 'Must be 80 characters or fewer');
const LastNameSchema = z
    .string()
    .trim()
    .min(1, 'Enter your last name')
    .max(80, 'Must be 80 characters or fewer');
const EmailSchema = z.string().trim().email('Enter a valid email address');
const ConfirmPasswordSchema = z.string().min(1, 'Confirm your password');
const optionalTrimmed = z
    .string()
    .trim()
    .transform((value) => (value === '' ? undefined : value))
    .optional();
const MFACodeSchema = optionalTrimmed.refine((value) => !value || value.length === 6, {
    message: 'Enter a 6 digit code',
});
const MFARecoverySchema = optionalTrimmed.refine((value) => !value || value.length >= 8, {
    message: 'Recovery code must be at least 8 characters',
});

const LoginFormSchema = z.object({
    email: EmailSchema,
    password: PasswordSchema,
    mfaCode: MFACodeSchema,
    mfaRecovery: MFARecoverySchema,
});
const SignupFormSchema = z
    .object({
        firstName: FirstNameSchema,
        lastName: LastNameSchema,
        email: EmailSchema,
        password: PasswordSchema,
        confirmPassword: ConfirmPasswordSchema,
    })
    .superRefine((value, ctx) => {
        if (value.password !== value.confirmPassword) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['confirmPassword'],
                message: 'Passwords must match',
            });
        }
    });
const ForgotFormSchema = z.object({
    email: EmailSchema,
});
const ResetFormSchema = z
    .object({
        password: PasswordSchema,
        confirmPassword: z.string(),
    })
    .refine((value) => value.password === value.confirmPassword, {
        path: ['confirmPassword'],
        message: 'Passwords must match',
    });
const OAuthMfaFormSchema = z
    .object({
        mfaCode: MFACodeSchema,
        mfaRecovery: MFARecoverySchema,
    })
    .refine((value) => Boolean(value.mfaCode || value.mfaRecovery), {
        path: ['mfaCode'],
        message: 'Enter your authenticator code or recovery code',
    });

type LoginFormValues = z.infer<typeof LoginFormSchema>;
type SignupFormValues = z.infer<typeof SignupFormSchema>;
type ForgotFormValues = z.infer<typeof ForgotFormSchema>;
type ResetFormValues = z.infer<typeof ResetFormSchema>;
type OAuthMfaFormValues = z.infer<typeof OAuthMfaFormSchema>;

const defaultForm: FormState = {
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    mfaCode: '',
    mfaRecovery: '',
};

function getMode(pathname: string): Mode {
    if (pathname.endsWith('/signup')) return 'signup';
    if (pathname.endsWith('/forgot-password')) return 'forgot-password';
    if (pathname.endsWith('/reset-password')) return 'reset-password';
    if (pathname.endsWith('/verify-email')) return 'verify-email';
    return 'login';
}

function isFormField(value: string): value is FormField {
    return formFields.includes(value as FormField);
}

function mapZodErrors(error: z.ZodError): FormErrors {
    const fieldErrors = error.flatten().fieldErrors;
    return Object.entries(fieldErrors).reduce<FormErrors>((next, [field, messages]) => {
        if (isFormField(field) && messages?.[0]) {
            next[field] = messages[0];
        }
        return next;
    }, {});
}

function schemaErrorMessage(schema: z.ZodTypeAny, value: unknown, fallbackMessage: string) {
    const result = schema.safeParse(value);
    if (result.success) return null;
    return result.error.issues[0]?.message || fallbackMessage;
}

function validateForMode(mode: Mode, values: FormState) {
    const result =
        mode === 'signup'
            ? SignupFormSchema.safeParse(values)
            : mode === 'forgot-password'
              ? ForgotFormSchema.safeParse(values)
              : mode === 'reset-password'
                ? ResetFormSchema.safeParse(values)
                : mode === 'login'
                  ? LoginFormSchema.safeParse(values)
                  : z.object({}).safeParse({});

    if (result.success) {
        return { success: true as const, data: result.data, errors: {} };
    }

    return { success: false as const, data: null, errors: mapZodErrors(result.error) };
}

function evaluatePassword(password: string) {
    return passwordRequirements.map((rule) => ({
        ...rule,
        met: rule.test(password),
    }));
}

function authFeedback(message: string, type: AuthFeedback['type'] = 'info'): AuthFeedback {
    return { message, type };
}

function getAuthErrorType(error: unknown): AuthErrorType | undefined {
    if (!error || typeof error !== 'object') return undefined;
    const code = (error as Partial<AuthRequestError>).code;
    return typeof code === 'string' && code.trim() ? code : undefined;
}

function getAuthErrorMessage(error: unknown, fallback: string) {
    const code = getAuthErrorType(error);
    const message = error instanceof Error ? error.message : '';

    if (code === 'invalid_token') {
        return 'This reset link is invalid or has expired. Request a new one.';
    }

    if (code === 'invalid_payload' || message === 'invalid_payload') {
        return 'Check the form and try again.';
    }

    if (code === 'invalid_email' || message === 'invalid_email') {
        return 'Enter a valid email address';
    }

    return message || fallback;
}

function authErrorFeedback(error: unknown, fallback = 'Something went wrong'): AuthFeedback {
    return {
        message: getAuthErrorMessage(error, fallback),
        type: 'error',
        errorType: getAuthErrorType(error) ?? 'unknown',
    };
}

async function postJson(path: string, payload: Record<string, unknown>) {
    const res = await fetch(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
        const message = body?.error?.message || body?.message || 'Something went wrong';
        const code = body?.error?.code || body?.code;
        throw Object.assign(new Error(message), { code, status: res.status });
    }
    return body || {};
}

function startDevelopmentRefresh() {
    if (process.env.NODE_ENV === 'production') return;

    let currentVersion = '';

    window.setInterval(() => {
        void fetch('/auth/dev-build-id', { cache: 'no-store' })
            .then((res) => (res.ok ? res.json() : null))
            .then((body: DevBuildVersionResponse | null) => {
                if (!body?.version) return;
                if (currentVersion && currentVersion !== body.version) {
                    window.location.reload();
                    return;
                }
                currentVersion = body.version;
            })
            .catch(() => undefined);
    }, developmentRefreshIntervalMs);
}

function normalizeReturnPath(url: string) {
    try {
        const parsed = new URL(url);
        return parsed.hostname.replace(/^www\./, '');
    } catch {
        return 'your app';
    }
}

function buildAuthHref(mode: Mode, client?: string, returnTo?: string) {
    const params = new URLSearchParams();
    if (client) params.set('client', client);
    if (returnTo) params.set('returnTo', returnTo);
    const query = params.toString();
    return `/auth/${mode}${query ? `?${query}` : ''}`;
}

function buildOAuthStartHref(
    provider: 'google' | 'apple',
    intent: 'login' | 'signup',
    client?: string,
    returnTo?: string,
) {
    const params = new URLSearchParams({ intent });
    if (client) params.set('client', client);
    if (returnTo) params.set('returnTo', returnTo);
    return `/auth/oauth/${provider}/start?${params.toString()}`;
}

function providerLabel(provider?: string | null) {
    if (provider === 'google') return 'Google';
    if (provider === 'apple') return 'Apple';
    return 'this provider';
}

function oauthErrorMessage(error: string, provider?: string | null) {
    const label = providerLabel(provider);
    if (error === 'not_linked') {
        return `No linked ${label} account was found. Log in with your email and password, then link ${label} in Account settings.`;
    }
    if (error === 'email_in_use') {
        return `An Insureworks account already exists for that email. Log in with your email and password, then link ${label} in Account settings.`;
    }
    if (error === 'linked_to_other_account') {
        return `That ${label} account is already linked to another Insureworks account.`;
    }
    if (error === 'email_missing') {
        return `${label} did not provide an email address. Use email signup or try a different ${label} account.`;
    }
    if (error === 'cancelled') {
        return `${label} sign-in was cancelled.`;
    }
    return `We could not complete ${label} sign-in. Please try again.`;
}

function getValidationField(mode: Mode, field: FormField): ValidationField {
    if (field === 'email') {
        return {
            required: true,
            type: 'email',
            validate: (value) =>
                schemaErrorMessage(EmailSchema, value, 'Enter a valid email address') || true,
        };
    }

    if (field === 'password') {
        return {
            required: true,
            type: 'text',
            validate: (value) =>
                schemaErrorMessage(PasswordSchema, value, 'Enter your password') || true,
        };
    }

    if (field === 'confirmPassword') {
        return {
            required: true,
            type: 'text',
            validate: (value, currentValues) => {
                const requiredMessage = schemaErrorMessage(
                    ConfirmPasswordSchema,
                    value,
                    'Confirm your password',
                );
                if (requiredMessage) return requiredMessage;
                return value === currentValues['password'] || 'Passwords must match';
            },
        };
    }

    if (field === 'firstName') {
        return {
            required: mode === 'signup',
            type: 'text',
            validate: (value) =>
                schemaErrorMessage(FirstNameSchema, value, 'Enter your first name') || true,
        };
    }

    if (field === 'lastName') {
        return {
            required: mode === 'signup',
            type: 'text',
            validate: (value) =>
                schemaErrorMessage(LastNameSchema, value, 'Enter your last name') || true,
        };
    }

    if (field === 'mfaCode') {
        return {
            type: 'text',
            validate: (value) =>
                schemaErrorMessage(MFACodeSchema, value, 'Enter a 6 digit code') || true,
        };
    }

    return {
        type: 'text',
        validate: (value) =>
            schemaErrorMessage(
                MFARecoverySchema,
                value,
                'Recovery code must be at least 8 characters',
            ) || true,
    };
}

function shouldHideValidationWhenEmpty(field: FormField) {
    return field === 'mfaCode' || field === 'mfaRecovery';
}

function App() {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [form, setForm] = useState<FormState>(defaultForm);
    const [fieldErrors, setFieldErrors] = useState<FormErrors>({});
    const [touched, setTouched] = useState<TouchedFields>({});
    const [dirty, setDirty] = useState<DirtyFields>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [config, setConfig] = useState<AppConfig | null>(null);
    const [message, setMessage] = useState<AuthFeedback | null>(null);
    const [error, setError] = useState<AuthFeedback | null>(null);
    const [resendMessage, setResendMessage] = useState<AuthFeedback | null>(null);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [verificationPending, setVerificationPending] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [checkingSession, setCheckingSession] = useState(false);
    const mode = getMode(location.pathname);
    const token = searchParams.get('token') || '';
    const clientParam = searchParams.get('client') || '';
    const client = clientParam || 'spark';
    const returnTo = searchParams.get('returnTo') || '';
    const oauthError = searchParams.get('oauthError') || '';
    const oauthProvider = searchParams.get('provider') || '';
    const oauthMfa = searchParams.get('oauthMfa') || '';
    const oauthMfaPending = mode === 'login' && Boolean(oauthMfa);
    const authHref = (targetMode: Mode) => buildAuthHref(targetMode, clientParam, returnTo);
    const oauthStartHref = (provider: 'google' | 'apple', intent: 'login' | 'signup') =>
        buildOAuthStartHref(provider, intent, client, returnTo);
    const destination = config ? normalizeReturnPath(config.returnTo) : 'your app';
    const oauthProviders = config?.oauthProviders ?? [];
    const requirements = useMemo(() => evaluatePassword(form.password), [form.password]);

    useEffect(() => {
        setFieldErrors({});
        setTouched({});
        setDirty({});
        setSubmitAttempted(false);
        setMfaRequired(oauthMfaPending);
        setVerificationPending(false);
        setResendMessage(null);
        setMessage(null);
        setError(null);
    }, [mode, oauthMfaPending]);

    useEffect(() => {
        if (oauthMfaPending) {
            setMfaRequired(true);
            setMessage(
                authFeedback(
                    `Enter your authenticator code or recovery code to continue with ${providerLabel(
                        oauthProvider,
                    )}.`,
                    'info',
                ),
            );
            setError(null);
            return;
        }
        if (oauthError) {
            setError({
                message: oauthErrorMessage(oauthError, oauthProvider),
                type: 'error',
                errorType: oauthError,
            });
        }
    }, [oauthError, oauthProvider, oauthMfaPending]);

    useEffect(() => {
        const params = new URLSearchParams({ client });
        if (returnTo) params.set('returnTo', returnTo);
        fetch(`/auth/config?${params.toString()}`, { credentials: 'include' })
            .then((res) => res.json())
            .then(setConfig)
            .catch(() => setConfig(null));
    }, [client, returnTo]);

    useEffect(() => {
        const shouldCheckSession = (mode === 'login' || mode === 'signup') && !oauthMfaPending;
        if (!shouldCheckSession) {
            setCheckingSession(false);
            return;
        }

        let active = true;
        setCheckingSession(true);
        postJson('/auth/api/session', { client, returnTo })
            .then((body: SessionStatusResponse | null) => {
                if (!active) return;
                if (body?.authenticated && body.redirectTo) {
                    window.location.assign(body.redirectTo);
                    return;
                }
                setCheckingSession(false);
            })
            .catch(() => {
                if (active) setCheckingSession(false);
            });

        return () => {
            active = false;
        };
    }, [mode, client, returnTo, oauthMfaPending]);

    useEffect(() => {
        if (mode !== 'verify-email') return;
        if (!token) {
            setError({
                message: 'Your verification link is missing or invalid. Request a new link below.',
                type: 'error',
                errorType: 'missing_token',
            });
            return;
        }
        setLoading(true);
        postJson('/auth/api/verify-email', { token })
            .then(() =>
                setMessage(
                    authFeedback(
                        'Your email address has been verified. You can log in now.',
                        'success',
                    ),
                ),
            )
            .catch((err) =>
                setError(authErrorFeedback(err, 'Could not verify email. Please try again.')),
            )
            .finally(() => setLoading(false));
    }, [mode, token]);

    useEffect(() => {
        if (mode === 'reset-password' && !token) {
            setError({
                message: 'Your reset link is missing or has expired.',
                type: 'error',
                errorType: 'missing_token',
            });
        }
    }, [mode, token]);

    const update = (field: FormField) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = { ...form, [field]: event.target.value };
        setForm(next);
        setDirty((current) => ({ ...current, [field]: true }));

        const shouldValidate =
            submitAttempted ||
            touched[field] ||
            (field === 'password' && (touched.confirmPassword || touched.password)) ||
            (field === 'confirmPassword' && (touched.password || touched.confirmPassword));

        if (shouldValidate) {
            setFieldErrors(validateForMode(mode, next).errors);
        }
    };

    const markTouched = (field: FormField) => () => {
        setTouched((current) => ({ ...current, [field]: true }));
        setFieldErrors(validateForMode(mode, form).errors);
    };

    const getFieldError = (field: FormField) => {
        if (!submitAttempted && !touched[field]) return undefined;
        return fieldErrors[field];
    };

    const getFieldValidationState = (field: FormField) => {
        const validationField = getValidationField(mode, field);
        const value = form[field];

        if (shouldHideValidationWhenEmpty(field) && isFieldValueEmpty(validationField, value)) {
            return null;
        }

        return resolveFieldValidationState({
            field: validationField,
            value,
            values: { ...form },
            error: fieldErrors[field],
            touched: Boolean(touched[field]),
            dirty: Boolean(dirty[field]),
            submitAttempted,
        });
    };

    const submitLogin = async (values: LoginFormValues) => {
        const body = await postJson('/auth/api/login', {
            client,
            returnTo,
            email: values.email,
            password: values.password,
            mfaCode: values.mfaCode,
            mfaRecovery: values.mfaRecovery,
        });
        window.location.assign(body.redirectTo || config?.returnTo || '/');
    };

    const submitSignup = async (values: SignupFormValues) => {
        const body = await postJson('/auth/api/signup', {
            client,
            returnTo,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            password: values.password,
        });
        if (body.status === 'verification_required') {
            setMessage(
                authFeedback(
                    'Account created. Check your inbox to verify your email before logging in.',
                    'success',
                ),
            );
            return;
        }
        if (body.redirectTo) {
            window.location.assign(body.redirectTo);
            return;
        }
        window.location.assign(authHref('login'));
        setMessage(authFeedback('Account created. Log in to continue.', 'success'));
    };

    const submitForgot = async (values: ForgotFormValues) => {
        await postJson('/auth/api/password/forgot', {
            client,
            returnTo,
            email: values.email,
        });
        setMessage(
            authFeedback(
                "If an account matches that email, you'll receive reset instructions in the next few minutes.",
                'info',
            ),
        );
    };

    const submitReset = async (values: ResetFormValues) => {
        if (!token) {
            throw Object.assign(
                new Error('Reset link missing or expired. Request a new link to continue.'),
                { code: 'missing_token' as const },
            );
        }
        await postJson('/auth/api/password/reset', { token, password: values.password });
        setMessage(
            authFeedback('Your password has been reset. Log in with your new password.', 'success'),
        );
        window.location.assign(authHref('login'));
    };

    const submitOAuthMfa = async (values: OAuthMfaFormValues) => {
        if (!oauthMfa) {
            throw Object.assign(new Error('This sign-in attempt has expired. Start again.'), {
                code: 'oauth_mfa_expired' as const,
            });
        }
        const body = await postJson('/auth/api/oauth/mfa', {
            token: oauthMfa,
            mfaCode: values.mfaCode,
            mfaRecovery: values.mfaRecovery,
        });
        window.location.assign(body.redirectTo || config?.returnTo || '/');
    };

    const resendVerification = async () => {
        setResendMessage(null);
        const targetEmail = form.email.trim();

        if (!targetEmail) {
            setResendMessage({
                message: 'Enter your email above to resend the link.',
                type: 'error',
                errorType: 'missing_email',
            });
            return;
        }

        setResendLoading(true);
        try {
            await postJson('/auth/api/verify-email/resend', {
                client,
                returnTo,
                email: targetEmail,
            });
            setResendMessage(authFeedback('Verification email sent. Check your inbox.', 'success'));
        } catch (err) {
            setResendMessage(authErrorFeedback(err, 'Could not resend verification email'));
        } finally {
            setResendLoading(false);
        }
    };

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();
        setError(null);
        setMessage(null);
        setResendMessage(null);
        setVerificationPending(false);
        setSubmitAttempted(true);

        const validation = oauthMfaPending
            ? (() => {
                  const result = OAuthMfaFormSchema.safeParse(form);
                  if (result.success) {
                      return { success: true as const, data: result.data, errors: {} };
                  }
                  return {
                      success: false as const,
                      data: null,
                      errors: mapZodErrors(result.error),
                  };
              })()
            : validateForMode(mode, form);
        setFieldErrors(validation.errors);
        if (!validation.success) return;

        setLoading(true);
        try {
            if (oauthMfaPending) await submitOAuthMfa(validation.data as OAuthMfaFormValues);
            else if (mode === 'signup') await submitSignup(validation.data as SignupFormValues);
            else if (mode === 'forgot-password')
                await submitForgot(validation.data as ForgotFormValues);
            else if (mode === 'reset-password')
                await submitReset(validation.data as ResetFormValues);
            else await submitLogin(validation.data as LoginFormValues);
        } catch (err: any) {
            if (err?.code === 'mfa_required') {
                setMfaRequired(true);
                setError({
                    message: 'Enter your authenticator code or recovery code to continue.',
                    type: 'error',
                    errorType: 'mfa_required',
                });
            } else if (err?.code === 'email_unverified') {
                setVerificationPending(true);
                setError(null);
            } else {
                setError(authErrorFeedback(err));
            }
        } finally {
            setLoading(false);
        }
    };

    const signupDisabled = config?.client.signupPolicy === 'disabled';
    const signupEnabled = config?.client.signupPolicy !== 'disabled';
    const templateProps: AuthTemplateProps = {
        destination,
        message,
        error,
        resendMessage,
        oauthProviders,
        oauthMfaPending,
        form,
        requirements,
        mfaRequired,
        verificationPending,
        signupDisabled,
        signupEnabled,
        loading,
        resendLoading,
        fieldErrorVariant: 'border',
        authHref,
        oauthStartHref,
        update,
        markTouched,
        getFieldError,
        getFieldValidationState,
        onResendVerification: resendVerification,
        onSubmit,
    };

    let template;

    if (checkingSession && (mode === 'login' || mode === 'signup')) {
        template = (
            <section className="mx-auto max-w-lg space-y-4">
                <div className="p-8 bg-coconut rounded-lg space-y-3">
                    <h1 className="text-2xl font-semibold">Checking your session</h1>
                    <p className="text-base text-licorice">Taking you back to {destination}.</p>
                </div>
            </section>
        );
    } else {
        switch (mode) {
            case 'login':
                template = <LoginTemplate {...templateProps} />;
                break;
            case 'signup':
                template = <SignupTemplate {...templateProps} />;
                break;
            case 'forgot-password':
                template = <ForgotPasswordTemplate {...templateProps} />;
                break;
            case 'reset-password':
                template = <ResetPasswordTemplate {...templateProps} />;
                break;
            case 'verify-email':
                template = (
                    <VerifyEmailTemplate message={message} error={error} authHref={authHref} />
                );
                break;
        }
    }

    return (
        <div className="auth-shell min-h-screen flex flex-col relative bg-acai overflow-hidden">
            <AnimatedScribble
                type={authBackgroundScribbleType}
                durationMs={1800}
                delayMs={250}
                className="w-auth max-w-[4000px] h-screen min-h-180 absolute top-0 left-1/2 transform -translate-x-1/4 translate-y-[-3.5%] z-0 text-dragonfruit"
            />

            <SiteHeader />
            <main className="relative z-10 flex-1 mx-auto w-full max-w-5xl p-4">{template}</main>
            <SiteFooter />
        </div>
    );
}

startDevelopmentRefresh();
createRoot(document.getElementById('root') as HTMLElement).render(
    <BrowserRouter>
        <App />
    </BrowserRouter>,
);
